import { GlobalTagRecordType, IValueBoolean, TClientTags, TTagType } from "@/ts/common-types";
import {
    getClientTagsList,
    normalizeTagType,
    updateTagMapping,
    validateRemoveTag,
    validateRemoveTagType,
    getTagTypeColorIndex,
} from "@/ts/utils";
import { Button, Card, Col, Input, Row, Select, Tag, Spin } from "antd";
import {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from "react";
import { filter, find, map, some, uniq, debounce } from "underscore";
import * as Sentry from "@sentry/react";
import { useDispatch, useSelector } from "react-redux";
import TagsMappingComponent from "./TagsMappingComponent";
import NoDataMessage from "@/components/misc/NoDataMessage";
import { forEach } from "underscore";
import { showToast } from "@/ts/modals";
import {
    setIsAddTagTypeFormSubmitted,
    updateTagTypeList,
} from "@/features/clientDataSlice";
import { CloseOutlined } from "@ant-design/icons";
import { TAG_COLORS, TaggingActionTypes } from "@/ts/constants";
import { usePermissionCheck } from "@/hooks/AllCustomHooks";
import { FixedSizeList as List } from "react-window";

interface ITagsComponentProps {
    isAddTagTypeModalVisible: boolean;
    setIsTagTypeModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedTagType: string;
    setSelectedTagType: Dispatch<SetStateAction<string>>;
    tagTypes: TTagType[];
    setTagTypes: Dispatch<SetStateAction<TTagType[]>>;
}

const { Option } = Select;

const TagsComponent = ({
    isAddTagTypeModalVisible,
    setIsTagTypeModalVisible,
    selectedTagType,
    setSelectedTagType,
    tagTypes,
    setTagTypes,
}: ITagsComponentProps) => {
    const dispatch = useDispatch();
    const [isTagsListingSreen, setIsTagsListingSreen] = useState<boolean>(true);
    const tagTypeMapRef = useRef(new Map<number, string[]>());
    const [inputValues, setInputValues] = useState<{ [key: number]: string }>(
        {}
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const isAddTagTypeFormSubmitted = useSelector(
        (state: { isAddTagTypeFormSubmitted: IValueBoolean }) =>
            state.isAddTagTypeFormSubmitted.value
    );

    const tagTypeListLoading = useSelector(
        (state: { tagTypeListLoading: { value: boolean } }) =>
            state.tagTypeListLoading.value
    );

    const {
        canListTagType,
        canAddTagType,
        canDeleteTagType,
        canCreateTag,
        canDeleteTags,
    } = usePermissionCheck();

    // Calculate global tag count for color offset
    const globalTagCount = useMemo(() => {
        return filter(tagTypeList, (type) => type.record_type === GlobalTagRecordType.GLOBAL).length;
    }, [tagTypeList]);

    // Memoize the tag lists to prevent unnecessary recalculations
    const tagLists = useMemo(() => {
        const tagsMap = new Map<number, string[]>();
        forEach(tagTypeList, (tagType) =>
            tagsMap.set(tagType.tag_type_id, tagType.tags_map)
        );
        return tagsMap;
    }, [tagTypeList]);

    // Update ref when tagLists changes
    useEffect(() => {
        tagTypeMapRef.current = tagLists;
    }, [tagLists]);

    // Memoize getTagsList
    const getTagsList = useCallback(
        (tagTypeId: number) =>
            filter(tagTypeList, (type) => type.tag_type_id === tagTypeId),
        [tagTypeList]
    );

    const getUpdatedTagsList = useCallback(
        (
            tagType: string,
            tag: string,
            requestType: TaggingActionTypes.ADD | TaggingActionTypes.REMOVE,
            tagId?: number
        ) => {
            const updatedTagTypeList = map(tagTypeList, (type) =>
                normalizeTagType(type.tag_type) === normalizeTagType(tagType)
                    ? {
                          ...type,
                          tags:
                              requestType === TaggingActionTypes.ADD
                                  ? uniq([...type.tags, tag])
                                  : filter(
                                        type.tags,
                                        (existingTag) => existingTag !== tag
                                    ),

                          tags_map:
                              requestType === TaggingActionTypes.ADD
                                  ? uniq([
                                        ...type.tags_map,
                                        `${tagId}##${tag}`,
                                    ])
                                  : filter(type.tags_map, (entry) => {
                                        const [, tagText] = entry.split("##");
                                        return tagText !== tag;
                                    }),
                      }
                    : type
            );

            dispatch(updateTagTypeList(updatedTagTypeList));
        },
        [tagTypeList, dispatch]
    );

    // Helper for filtering tags by search value
    const filterTags = (tagsList: string[], value: string) =>
        value
            ? filter(tagsList, (data) =>
                data.split("##")[1].toLowerCase().startsWith(value.toLowerCase())
            )
            : tagsList;

    // Create a stable debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((searchedTagTypeId: number, value: string) => {
                const initialTagsList =
                    find(
                        getTagsList(searchedTagTypeId),
                        (tagType) => tagType.record_type === selectedTagType
                    )?.tags_map || [];

                const filteredTags = filterTags(initialTagsList, value);

                const newMap = new Map(tagTypeMapRef.current);
                newMap.set(searchedTagTypeId, filteredTags);
                tagTypeMapRef.current = newMap;
            }, 300),
        [selectedTagType, getTagsList]
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleRemoveTag = useCallback(
        async (tagType: string, tagToRemove: string, tagId: number) => {
            const { isTagPresent, message } = await validateRemoveTag(tagId);

            const isRemoveTag = window.confirm(
                `${isTagPresent
                    ? `${tagToRemove} is mapped to ${message} across subgroup.`
                    : ""
                } Are you sure you want to remove ${isTagPresent ? "it" : `${tagToRemove}`
                } from ${tagType} type?`
            );

            if (isRemoveTag) {
                const response = await updateTagMapping(
                    {
                        tag: tagToRemove,
                        tagId: tagId,
                        tagType,
                    },
                    "remove"
                );

                if (response.status) {
                    getUpdatedTagsList(tagType, tagToRemove, TaggingActionTypes.REMOVE);
                    setInputValues((prev) => ({
                        ...prev,
                        [tagType]: "",
                    }));
                }
            }
        },
        [selectedTagType, getUpdatedTagsList]
    );

    // Use filterTags helper in handleInputChange and debouncedSearch
    const handleInputChange = useCallback(
        (tagTypeId: number, value: string) => {
            setInputValues((prev) => ({
                ...prev,
                [tagTypeId]: value,
            }));

            const initialTagsList =
                find(
                    getTagsList(tagTypeId),
                    (type) => type.record_type === selectedTagType
                )?.tags_map || [];

            const filteredTags = filterTags(initialTagsList, value);

            const newMap = new Map(tagTypeMapRef.current);
            newMap.set(tagTypeId, filteredTags);
            tagTypeMapRef.current = newMap;

            debouncedSearch(tagTypeId, value);
        },
        [debouncedSearch, selectedTagType, getTagsList]
    );

    // Memoize handleRecordTypeSelection
    const handleRecordTypeSelection = useCallback(
        (value: string) => {
            setSelectedTagType(value);
            setTagTypes(
                map(
                    filter(
                        tagTypeList,
                        (tagType) => tagType.record_type === value
                    ),
                    (type) => ({
                        id: type.tag_type_id,
                        tag_type: type.tag_type,
                        is_default: type.is_default,
                    })
                )
            );
        },
        [setSelectedTagType, setTagTypes, tagTypeList]
    );

    // Memoize checkIfTagExist
    const checkIfTagExist = useCallback(
        (tagtoAdd: string) => {
            const result = find(
                filter(tagTypeList, { record_type: selectedTagType }),
                (type) =>
                    some(type.tags, (tag) => tag.toLowerCase() === tagtoAdd)
            );
            return result?.tag_type || null;
        },
        [tagTypeList, selectedTagType]
    );

    // Memoize handleModalDisplayChange
    const handleModalDisplayChange = useCallback(() => {
        setIsTagTypeModalVisible((prev) => !prev);
    }, [setIsTagTypeModalVisible]);

    // Memoize handeTagsListingScreenChange
    const handeTagsListingScreenChange = useCallback(() => {
        setIsTagsListingSreen((prev) => !prev);
    }, []);

    // Memoize handleRemoveTagType
    const handleRemoveTagType = useCallback(
        async (tagType: string, tagTypeId: number) => {
            const tagsList =
                find(
                    tagTypeList,
                    (type) =>
                        type.tag_type === tagType &&
                        type.record_type === selectedTagType
                )?.tags || [];

            const { isTagPresent, message } = tagsList.length
                ? await validateRemoveTagType(tagType)
                : {
                    isTagPresent: false,
                    message: "",
                };

            const isRemoveTagType = window.confirm(
                `${isTagPresent
                    ? ` ${tagType} tags are mapped to ${message} across subgroup.`
                    : ""
                } Are you sure you want to remove ${tagType} type?`
            );

            if (isRemoveTagType) {
                const response = await updateTagMapping(
                    {
                        tagType,
                        tagTypeId
                    },
                    "type-remove"
                );

                if (response.status) {
                    dispatch(setIsAddTagTypeFormSubmitted(true));
                }
            }
        },
        [tagTypeList, selectedTagType, dispatch]
    );

    const handleInputConfirm = useCallback(
        async (tagType: TTagType) => {
            const tagToAdd = inputValues[tagType.id];
            if (tagToAdd) {
                const existingTagType = checkIfTagExist(tagToAdd.toLowerCase());

                if (existingTagType) {
                    showToast(
                        "danger",
                        `${tagToAdd} is already mapped to ${existingTagType} tag type`
                    );
                } else {
                    const response = await updateTagMapping(
                        {
                            tag: tagToAdd,
                            tagTypeId: tagType.id,
                            tagType: tagType.tag_type,
                        },
                        "add"
                    );
                    if (response.status) {
                        setInputValues((prev) => ({
                            ...prev,
                            [tagType.id]: "",
                        }));
                        try {
                            await getClientTagsList();
                        } catch (error) {
                            console.error("Error refetching tags:", error);
                            Sentry.captureException(error);
                        }
                    }
                }
            }
        },
        [inputValues, selectedTagType, checkIfTagExist]
    );

    // TagItem as a regular function
    function TagItem({
        tag,
        tagType,
        tagTypeId,
        tagTypeIndex,
    }: {
        tag: string;
        tagType: string;
        tagTypeId: number;
        tagTypeIndex: number;
        }) {
        const colorIndex = getTagTypeColorIndex(selectedTagType, tagTypeIndex, globalTagCount);
        const { fill, border, background } = TAG_COLORS[colorIndex];
        const [tagId, tagText] = tag.split("##");
        return (
            <Tag
                key={`${tagTypeId}-${tagId}`}
                closeIcon={canDeleteTags}
                onClose={() =>
                    canDeleteTags
                        ? handleRemoveTag(tagType, tagText, +tagId)
                        : null
                }
                className="d-flex align-items-center justify-content-between py-1 px-2 mt-1"
                style={{
                    color: `${fill}`,
                    border: `1px solid ${border}`,
                    backgroundColor: `${background}`,
                    borderRadius: 8,
                    maxWidth: "80%",
                    boxSizing: "border-box",
                }}
            >
                <span
                    className="me-2 text-truncate"
                    title={`${tagText}`}
                >
                    {tagText}
                </span>
            </Tag>
        );
    }

    // TagList as a regular function
    function TagList({
        tagsList,
        tagType,
        tagTypeId,
        tagTypeIndex,
    }: {
        tagsList: string[];
        tagType: string;
        tagTypeId: number;
        tagTypeIndex: number;
        }) {
        const tagRow = ({ index, style }: { index: number; style: React.CSSProperties }) => (
            <div style={style}>
                <TagItem
                    tag={tagsList[index]}
                    tagType={tagType}
                    tagTypeId={tagTypeId}
                    tagTypeIndex={tagTypeIndex}
                />
            </div>
        );
        return (
            <List
                height={190}
                itemCount={tagsList.length}
                itemSize={35}
                width={"100%"}
                overscanCount={5}
                useIsScrolling={true}
                style={{ width: "100%" }}
            >
                {tagRow}
            </List>
        );
    }

    // renderTags as a regular function
    function renderTags(tagType: TTagType, tagTypeIndex: number) {
        // Use filtered tags if searching, else use full list
        const isSearching = !!inputValues[tagType.id];
        const tags = isSearching
            ? tagTypeMapRef.current.get(tagType.id) || []
            : tagLists.get(tagType.id) || [];
        const tagCount = tags.length;

        return (
            <Card
                className="border"
                style={{
                    height: "360px",
                    width: "290px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }}
                key={tagType.id}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <strong style={{ fontSize: "16px" }}>{tagType.tag_type}</strong>
                    {canDeleteTagType ? (
                        <CloseOutlined
                            onClick={() => handleRemoveTagType(tagType.tag_type, tagType.id)}
                            style={{
                                fontSize: "14px",
                                cursor: "pointer",
                                display: tagType.is_default ? "none" : "unset",
                            }}
                        />
                    ) : (
                        ""
                    )}
                </div>

                {!canCreateTag ? (
                    ""
                ) : (
                    <Input
                        value={inputValues[tagType.id]}
                        onChange={(e) => handleInputChange(tagType.id, e.target.value)}
                        onPressEnter={() => handleInputConfirm(tagType)}
                        placeholder="Add new tag"
                        className="py-2 px-3 w-100 mt-4"
                        style={{
                            outline: "none",
                            borderStyle: "dashed",
                            borderWidth: "2px",
                            borderColor: "#d9d9d9",
                            borderRadius: "15px",
                        }}
                    />
                )}

                <div
                    className="mt-3"
                    style={{
                        height: 190,
                        overflowY: tagCount > 0 ? "auto" : "hidden",
                        overflowX: "hidden",
                        width: "100%",
                        position: "relative",
                    }}
                >
                    {tagCount > 0 ? (
                        <TagList
                            tagsList={tags}
                            tagType={tagType.tag_type}
                            tagTypeId={tagType.id}
                            tagTypeIndex={tagTypeIndex}
                        />
                    ) : (
                        <div
                            style={{
                                position: "absolute",
                                top: "40%",
                                left: 0,
                                width: "100%",
                                transform: "translateY(-50%)",
                                textAlign: "center",
                            }}
                            className="text-muted"
                        >
                            No tags
                        </div>
                    )}
                </div>

                <div className="text-end border-top py-1">
                    <span className="text-primary">{`Tags Count (${tagCount})`}</span>
                </div>
            </Card>
        );
    }

    // for setting tag types after a new tag type is added
    useEffect(() => {
        if (!isAddTagTypeModalVisible && isAddTagTypeFormSubmitted) {
            getClientTagsList()
                .then((clientTagTypes) => {
                    const recordTypeTags = map(
                        filter(
                            clientTagTypes,
                            (tagType) => tagType.record_type === selectedTagType
                        ),
                        (type) => ({
                            id: type.tag_type_id,
                            tag_type: type.tag_type,
                            is_default: type.is_default,
                        })
                    );
                    setTagTypes(recordTypeTags);
                })
                .catch((error) => {
                    console.error(error);
                    Sentry.captureException(error);
                });
        }
    }, [isAddTagTypeModalVisible, isAddTagTypeFormSubmitted]);

    if (tagTypeListLoading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "60vh", marginTop: 40 }}
            >
                <Spin size="large" tip="Loading tags..." />
            </div>
        );
    }

    return (
        <>
            <Row>
                <Col span={12}>
                    <h4>
                        {isTagsListingSreen ? "Tag Listing" : "Tag Mapping"}
                    </h4>
                </Col>
                <Col span={12}>
                    <Row>
                        <Col
                            className="d-flex align-items-center justify-content-end"
                            span={24}
                        >
                            {isTagsListingSreen ? (
                                <>
                                    <span>Record Type</span>
                                    <Select
                                        className="ms-2"
                                        value={selectedTagType}
                                        style={{
                                            width: 120,
                                        }}
                                        onChange={handleRecordTypeSelection}
                                        placeholder="Select Record Type"
                                    >
                                        <Option value={"global"}>Global</Option>
                                        <Option value={"campaign"}>
                                            Campaign
                                        </Option>
                                        <Option value={"product"}>
                                            Product
                                        </Option>
                                    </Select>
                                    <Button
                                        type="primary"
                                        className="mx-3"
                                        onClick={handleModalDisplayChange}
                                        disabled={!canAddTagType}
                                    >
                                        Add Tag Type
                                    </Button>
                                </>
                            ) : null}
                            <Button
                                type="primary"
                                onClick={handeTagsListingScreenChange}
                                style={{
                                    marginRight: 15,
                                    display: "none",
                                }}
                            >
                                {isTagsListingSreen
                                    ? "Tags Mapping"
                                    : "Add Tags"}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <div className="border-top pt-2 mt-2" />

            {isTagsListingSreen ? (
                <div className="d-flex flex-wrap gap-4 mt-4">
                    {tagTypes.length ? (
                        <>
                            {map(tagTypes, (tagType: TTagType, index: number) =>
                                renderTags(tagType, index)
                            )}
                        </>
                    ) : (
                        <NoDataMessage message="No tag types available" />
                    )}
                </div>
            ) : (
                    <>{canListTagType ? "" : <TagsMappingComponent />}</>
            )}
        </>
    );
};

export default TagsComponent;
